// src/context/UserDocContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { firebaseApp } from "../firebase/firebaseApp";

const UserDocContext = createContext(null);

export function UserDocProvider({ children, debug = false }) {
  const [state, setState] = useState({ loading: true, user: null, data: null });
  const unsubDocRef = useRef(null);
  const unsubAuthRef = useRef(null);

  useEffect(() => {
    const auth = getAuth(firebaseApp);
    const db = getFirestore(firebaseApp);

    unsubAuthRef.current = onAuthStateChanged(auth, (u) => {
      // tear down prior doc sub when user changes
      if (unsubDocRef.current) {
        unsubDocRef.current();
        unsubDocRef.current = null;
      }

      if (!u) {
        startTransition(() =>
          setState({ loading: false, user: null, data: null })
        );
        return;
      }

      // single subscription for the whole app
      const ref = doc(db, "lungform_users", u.uid);
      unsubDocRef.current = onSnapshot(
        ref,
        (snap) => {
          const data = snap.exists() ? snap.data() : {};
          // transition avoids blocking main thread on big renders
          startTransition(() => setState({ loading: false, user: u, data }));
          if (debug)
            console.log("[UserDoc] snapshot", {
              size: Object.keys(data || {}).length,
            });
        },
        (err) => {
          if (debug) console.warn("[UserDoc] onSnapshot error", err);
          startTransition(() => setState((s) => ({ ...s, loading: false })));
        }
      );
    });

    return () => {
      if (unsubDocRef.current) unsubDocRef.current();
      if (unsubAuthRef.current) unsubAuthRef.current();
    };
  }, [debug]);

  const value = useMemo(() => state, [state.loading, state.user, state.data]);
  return (
    <UserDocContext.Provider value={value}>{children}</UserDocContext.Provider>
  );
}

export function useUserDocState() {
  const ctx = useContext(UserDocContext);
  if (!ctx)
    throw new Error("useUserDocState must be used within UserDocProvider");
  return ctx; // { loading, user, data }
}
